// Import the TextSplitter class for handling text splitting.
import { TextSplitter } from './textSplitter.js';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Defines a class to create scroll-triggered animation effects on text.
export class BlurScrollEffect {
  constructor(textElement) {
    // Check if the provided element is valid.
    if (!textElement || !(textElement instanceof HTMLElement)) {
      throw new Error('Invalid text element provided.');
    }

    this.textElement = textElement;

    // Set up the effect for the provided text element.
    this.initializeEffect();
  }

  // Sets up the initial text effect on the provided element.
  initializeEffect() {
    // On smaller screens (mobile/tablet), skip the blur animation entirely
    // and show the text normally. This avoids cases where scroll-triggered
    // filters can leave the text blurred on some mobile browsers.
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this.textElement.style.filter = 'none';
      return;
    }

    // Callback to re-trigger animations on resize.
    const textResizeCallback = () => this.scroll();

    // Split text for animation and store the reference.
    // Use words-only splitting to reduce DOM complexity and improve scroll performance.
    this.splitter = new TextSplitter(this.textElement, {
      resizeCallback: textResizeCallback,
      splitTypeTypes: 'words'
    });
    
    // Trigger the initial scroll effect.
    this.scroll();
  }

  // Animates text based on the scroll position.
  scroll() {
    // Animate by word instead of individual character for smoother performance.
    const words = this.splitter.getWords();
    if (!words || !words.length) {
      return;
    }

    gsap.fromTo(
      words,
      {
        // Start slightly blurred but fully visible so mobile browsers
        // that handle filters differently still show readable text.
        filter: 'blur(6px)',
        willChange: 'filter',
      },
      {
        ease: 'none',
        filter: 'blur(0px)',
        stagger: 0.025,
        scrollTrigger: {
          trigger: this.textElement,
          // Start a bit before the text fully enters the viewport
          // (top of element is slightly below the bottom of the viewport)
          start: 'top 110%',
          // Finish as the bottom of the text approaches the middle of the viewport
          end: 'bottom 40%',
          scrub: 0.6,
        },
      }
    );
  }
}
